/*
 * Markdown toggles for the chat composer. Pure functions over (text, selection)
 * so they're easy to reason about: each returns the range to replace, the
 * replacement and the selection to restore. Only formats RichText renders.
 */

export type FormatKind = "bold" | "italic" | "underline" | "strike" | "code" | "codeblock" | "spoiler" | "quote";

export interface FormatEdit {
  /** Replace value[start, end) with text… */
  start: number;
  end: number;
  text: string;
  /** …then select [selStart, selEnd) in the new value. */
  selStart: number;
  selEnd: number;
}

interface InlineSpec {
  marker: string;
  /** Whether a run of `n` marker characters on both sides means this format is applied. */
  matches: (n: number) => boolean;
}

const INLINE: Record<Exclude<FormatKind, "codeblock" | "quote">, InlineSpec> = {
  bold: { marker: "**", matches: (n) => n >= 2 },
  // A single * (or the inner one of ***) is italic; ** alone is bold.
  italic: { marker: "*", matches: (n) => n === 1 || n >= 3 },
  underline: { marker: "__", matches: (n) => n >= 2 },
  strike: { marker: "~~", matches: (n) => n >= 2 },
  spoiler: { marker: "||", matches: (n) => n >= 2 },
  // Three backticks start a code block, not inline code.
  code: { marker: "`", matches: (n) => n === 1 || n === 2 },
};

function runBefore(s: string, i: number, ch: string) {
  let n = 0;
  while (i - n - 1 >= 0 && s[i - n - 1] === ch) n++;
  return n;
}
function runAfter(s: string, i: number, ch: string) {
  let n = 0;
  while (i + n < s.length && s[i + n] === ch) n++;
  return n;
}

const wrappedOutside = (v: string, s: number, e: number, spec: InlineSpec) => spec.matches(Math.min(runBefore(v, s, spec.marker[0]), runAfter(v, e, spec.marker[0])));

function wrappedInside(sel: string, spec: InlineSpec) {
  const L = spec.marker.length;
  if (sel.length <= 2 * L) return false;
  const ch = spec.marker[0];
  return spec.matches(Math.min(runAfter(sel, 0, ch), runBefore(sel, sel.length, ch)));
}

/** Shrink a selection so it doesn't start or end on whitespace (markers can't hug spaces). */
function trimSelection(v: string, s: number, e: number): [number, number] {
  while (s < e && /\s/.test(v[s])) s++;
  while (e > s && /\s/.test(v[e - 1])) e--;
  return [s, e];
}

function toggleInline(v: string, s0: number, e0: number, spec: InlineSpec): FormatEdit {
  const L = spec.marker.length;
  const [s, e] = s0 === e0 ? [s0, e0] : trimSelection(v, s0, e0);
  const sel = v.slice(s, e);

  if (s === e) {
    // Nothing selected: step out of an empty pair, or insert one with the caret inside.
    if (wrappedOutside(v, s, e, spec) && v.slice(s - L, s + L) === spec.marker + spec.marker) return { start: s - L, end: s + L, text: "", selStart: s - L, selEnd: s - L };
    return { start: s, end: e, text: spec.marker + spec.marker, selStart: s + L, selEnd: s + L };
  }

  if (sel.includes("\n")) {
    // Inline formats don't span lines: apply line by line.
    const lines = sel.split("\n");
    const filled = lines.filter((l) => l.trim());
    const allOn = filled.length > 0 && filled.every((l) => wrappedInside(l.trim(), spec));
    const text = lines
      .map((l) => {
        if (!l.trim()) return l;
        const lead = l.match(/^\s*/)![0];
        const trail = l.match(/\s*$/)![0];
        const core = l.trim();
        return lead + (allOn ? core.slice(L, -L) : spec.marker + core + spec.marker) + trail;
      })
      .join("\n");
    return { start: s, end: e, text, selStart: s, selEnd: s + text.length };
  }

  if (wrappedOutside(v, s, e, spec)) return { start: s - L, end: e + L, text: sel, selStart: s - L, selEnd: e - L };
  if (wrappedInside(sel, spec)) {
    const inner = sel.slice(L, sel.length - L);
    return { start: s, end: e, text: inner, selStart: s, selEnd: s + inner.length };
  }
  return { start: s, end: e, text: spec.marker + sel + spec.marker, selStart: s + L, selEnd: e + L };
}

function toggleCodeBlock(v: string, s: number, e: number): FormatEdit {
  const before = v.slice(0, s);
  const open = /```[a-z0-9]*\n$/i.exec(before);
  if (open && v.slice(e).startsWith("\n```")) {
    const start = s - open[0].length;
    return { start, end: e + 4, text: v.slice(s, e), selStart: start, selEnd: start + (e - s) };
  }
  const sel = v.slice(s, e);
  const inside = /^```[a-z0-9]*\n([\s\S]*)\n```$/i.exec(sel);
  if (inside) return { start: s, end: e, text: inside[1], selStart: s, selEnd: s + inside[1].length };
  const lead = s > 0 && v[s - 1] !== "\n" ? "\n" : "";
  const trail = e < v.length && v[e] !== "\n" ? "\n" : "";
  const text = `${lead}\`\`\`\n${sel}\n\`\`\`${trail}`;
  const innerStart = s + lead.length + 4;
  return { start: s, end: e, text, selStart: innerStart, selEnd: innerStart + sel.length };
}

function toggleQuote(v: string, s: number, e: number): FormatEdit {
  const ls = v.lastIndexOf("\n", s - 1) + 1;
  const nl = v.indexOf("\n", e > s && v[e - 1] === "\n" ? e - 1 : e);
  const le = nl === -1 ? v.length : nl;
  const lines = v.slice(ls, le).split("\n");
  const filled = lines.filter((l) => l.trim());
  const allOn = filled.length > 0 && filled.every((l) => l.startsWith("> "));
  const text = lines.map((l) => (!l.trim() ? l : allOn ? l.slice(2) : `> ${l}`)).join("\n");
  if (s === e) {
    const caret = Math.max(ls, s + (allOn ? -2 : 2));
    return { start: ls, end: le, text, selStart: caret, selEnd: caret };
  }
  return { start: ls, end: le, text, selStart: ls, selEnd: ls + text.length };
}

export function applyFormat(value: string, selStart: number, selEnd: number, kind: FormatKind): FormatEdit {
  const s = Math.min(selStart, selEnd);
  const e = Math.max(selStart, selEnd);
  if (kind === "codeblock") return toggleCodeBlock(value, s, e);
  if (kind === "quote") return toggleQuote(value, s, e);
  return toggleInline(value, s, e, INLINE[kind]);
}

/** Formats already applied to the selection (for the toolbar's pressed state). */
export function activeFormats(value: string, selStart: number, selEnd: number): Set<FormatKind> {
  const out = new Set<FormatKind>();
  const [s, e] = trimSelection(value, Math.min(selStart, selEnd), Math.max(selStart, selEnd));
  const sel = value.slice(s, e);
  for (const [kind, spec] of Object.entries(INLINE) as [keyof typeof INLINE, InlineSpec][]) {
    if (sel.includes("\n")) continue;
    if (wrappedOutside(value, s, e, spec) || wrappedInside(sel, spec)) out.add(kind);
  }
  if (/```[a-z0-9]*\n$/i.test(value.slice(0, s)) && value.slice(e).startsWith("\n```")) out.add("codeblock");
  const ls = value.lastIndexOf("\n", s - 1) + 1;
  const lines = value.slice(ls, e).split("\n").filter((l) => l.trim());
  if (lines.length && lines.every((l) => l.startsWith("> "))) out.add("quote");
  return out;
}

export const apply = (value: string, edit: FormatEdit) => value.slice(0, edit.start) + edit.text + value.slice(edit.end);

/* ───────────── Raw mention syntax (`\@name`) ───────────── */

/**
 * Like Discord, a backslash in front of a mention sends its raw syntax as text
 * instead of pinging anyone: `\@ada` → `<@1234567890>` (public user id),
 * `\@Moderators` → `<@&role-id>`, `\#general` → `<#channel-id>`. Code spans and
 * blocks are left alone. Anything that doesn't resolve keeps its backslash, and
 * the renderer shows it as plain, unlinked text.
 */
export interface RawRefLookup {
  /** Lower-case username → public id. */
  users: Map<string, string>;
  roles: { id: string; name: string }[];
  channels: { id: string; name: string }[];
}

const RAW_SCAN = /```[\s\S]*?```|`[^`\n]+`|\\([@#])/g;
const USERNAME_AT_START = /^[a-zA-Z0-9_]{2,24}(?![\w])/;

/** Escaped `\@` / `\#` positions outside code (an escaped backslash `\\@` doesn't count). */
function rawEscapes(text: string) {
  const out: { at: number; sigil: "@" | "#"; rest: string }[] = [];
  for (const m of text.matchAll(RAW_SCAN)) {
    if (!m[1] || text[m.index - 1] === "\\") continue;
    out.push({ at: m.index, sigil: m[1] as "@" | "#", rest: text.slice(m.index + 2) });
  }
  return out;
}

/** The longest name `rest` starts with (case-insensitive, ending at a word boundary). */
function longestName<T extends { name: string }>(rest: string, items: T[]): T | null {
  const lower = rest.toLowerCase();
  let best: T | null = null;
  for (const it of items) {
    const n = it.name.toLowerCase();
    if (!n || !lower.startsWith(n) || /[\w-]/.test(rest[n.length] ?? "")) continue;
    if (!best || n.length > best.name.length) best = it;
  }
  return best;
}

/** Usernames written as `\@name`, to resolve to public ids before sending. */
export function escapedUsernames(text: string): string[] {
  const names = new Set<string>();
  for (const e of rawEscapes(text)) {
    const word = e.sigil === "@" ? USERNAME_AT_START.exec(e.rest)?.[0].toLowerCase() : undefined;
    if (word && word !== "everyone" && word !== "here") names.add(word);
  }
  return [...names];
}

export function expandRawRefs(text: string, lookup: RawRefLookup): string {
  let out = "";
  let last = 0;
  for (const e of rawEscapes(text)) {
    if (e.at < last) continue;
    let hit: { token: string; length: number } | null = null;
    if (e.sigil === "#") {
      const ch = longestName(e.rest, lookup.channels);
      if (ch) hit = { token: `<#${ch.id}>`, length: ch.name.length };
    } else {
      const word = USERNAME_AT_START.exec(e.rest)?.[0];
      const publicId = word ? lookup.users.get(word.toLowerCase()) : undefined;
      const role = longestName(e.rest, lookup.roles);
      // A role whose name runs past the username ("\@Core team" vs @core) wins; otherwise the person.
      if (role && (!publicId || role.name.length > word!.length)) hit = { token: `<@&${role.id}>`, length: role.name.length };
      else if (publicId) hit = { token: `<@${publicId}>`, length: word!.length };
    }
    if (!hit) continue;
    out += text.slice(last, e.at) + hit.token;
    last = e.at + 2 + hit.length;
  }
  return out + text.slice(last);
}
